import * as React from 'react';
import { videos, VIDEO_BASE_URL } from '../../database/videos';
import './Videos.css';

const AUTO = -1;

/* One HLS player. Safari and iOS play HLS natively, so they get the playlist as
   a plain src and pick quality themselves (no menu). Everywhere else hls.js,
   loaded on demand so it stays out of the main bundle, feeds the <video>.
   autoStartLoad is off so six players fetch only their playlists on page load;
   segments start downloading when someone presses play. */
const VideoPlayer = ({ file, title }) => {
	const videoRef = React.useRef(null);
	const hlsRef = React.useRef(null);
	const [levels, setLevels] = React.useState([]);
	const [selected, setSelected] = React.useState(AUTO);
	const [playing, setPlaying] = React.useState(null);

	const src = `${VIDEO_BASE_URL}/${file}/master.m3u8`;

	React.useEffect(() => {
		const video = videoRef.current;
		let cancelled = false;

		if (video.canPlayType('application/vnd.apple.mpegurl')) {
			video.src = src;
			return undefined;
		}

		import('hls.js').then(({ default: Hls }) => {
			if (cancelled || !Hls.isSupported()) return;

			// capLevelToPlayerSize keeps a small grid card from pulling 1080p in
			// Auto; it lifts the cap again when the player goes fullscreen.
			const hls = new Hls({ autoStartLoad: false, capLevelToPlayerSize: true });
			hlsRef.current = hls;

			hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
				setLevels(data.levels.map((level, index) => ({ index, height: level.height })));
			});
			hls.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
				setPlaying(hls.levels[data.level]?.height ?? null);
			});
			hls.on(Hls.Events.ERROR, (_, data) => {
				if (!data.fatal) return;
				// Retrying only helps once a playlist has loaded; a missing or bad
				// manifest would otherwise retry forever.
				if (data.type === Hls.ErrorTypes.NETWORK_ERROR && hls.levels.length) hls.startLoad();
				else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
				else hls.destroy();
			});

			video.addEventListener('play', () => hls.startLoad(), { once: true });
			hls.loadSource(src);
			hls.attachMedia(video);
		});

		return () => {
			cancelled = true;
			hlsRef.current?.destroy();
			hlsRef.current = null;
		};
	}, [src]);

	const changeQuality = (event) => {
		const level = Number(event.target.value);
		setSelected(level);
		if (hlsRef.current) hlsRef.current.currentLevel = level;
	};

	const sortedLevels = [...levels].sort((a, b) => b.height - a.height);

	return (
		<figure className='kits-video-card'>
			<div className='kits-video-frame'>
				<video
					ref={videoRef}
					poster={`${VIDEO_BASE_URL}/${file}.jpg`}
					aria-label={title}
					preload='none'
					playsInline
					controls
				></video>
			</div>
			{/* figcaption has to be the figure's last child, so the quality menu
			    sits inside it rather than beside it. */}
			<figcaption className='kits-video-meta'>
				<span className='kits-video-caption'>{title}</span>
				{levels.length > 1 && (
					<select
						className='kits-video-quality'
						aria-label={`Video quality for ${title}`}
						value={selected}
						onChange={changeQuality}
					>
						<option value={AUTO}>
							{selected === AUTO && playing ? `Auto (${playing}p)` : 'Auto'}
						</option>
						{sortedLevels.map((level) => (
							<option key={level.index} value={level.index}>
								{level.height}p
							</option>
						))}
					</select>
				)}
			</figcaption>
		</figure>
	);
};

const Videos = () => (
	<section className='kits-videos' id='videos'>
		<div className='kits-videos-inner'>
			<span className='kit-badge'>How-To Library</span>
			<h2 className='kits-videos-title'>How-to videos</h2>
			<p className='kits-videos-desc'>
				Walk through the ScisGo™ workflow one step at a time.
			</p>

			<div className='kits-videos-grid'>
				{videos.map((video) => (
					<VideoPlayer key={video.file} file={video.file} title={video.title} />
				))}
			</div>
		</div>
	</section>
);

export default Videos;
